import { useRef, useCallback } from "react";

/**
 * Custom hook for managing focus across modal navigation
 * Handles focus for:
 * 1. Badge → Sheet (when sheet closes, focus returns to badge)
 * 2. Initial focus on close button when sheet opens
 * 3. Initial focus on back button when entering detail view
 */
export const useFocusManagement = () => {
  // Store direct reference to the element that triggered the sheet
  const sheetTriggerElementRef = useRef<HTMLElement | null>(null);
  // Store scroll position when sheet is opened (where badge was clicked)
  const sheetOpenScrollPositionRef = useRef({ x: 0, y: 0 });

  /**
   * Capture current scroll position (call this from click handler)
   */
  const captureScrollPosition = useCallback(() => {
    sheetOpenScrollPositionRef.current = {
      x: window.scrollX,
      y: window.scrollY,
    };
  }, []);

  /**
   * Set the element that triggered the sheet to open
   */
  const setSheetTriggerElement = useCallback((element: HTMLElement) => {
    sheetTriggerElementRef.current = element;
  }, []);

  /**
   * Restore focus to the badge button when sheet closes
   * Restores scroll position to where it was when the badge was clicked
   */
  const restoreFocusToSheetTrigger = useCallback(() => {
    if (sheetTriggerElementRef.current) {
      const { x: scrollX, y: scrollY } = sheetOpenScrollPositionRef.current;

      requestAnimationFrame(() => {
        sheetTriggerElementRef.current?.focus({ preventScroll: true });

        requestAnimationFrame(() => {
          window.scrollTo(scrollX, scrollY);
        });
      });
    }
  }, []);

  /**
   * Focus the close button when sheet first opens
   * @param buttonId - ID of the button to focus
   * @param shadowRoot - Optional shadow root to query within
   */
  const focusCloseButton = useCallback(
    (buttonId: string, shadowRoot?: ShadowRoot | null) => {
      const root = shadowRoot || document;
      const element = root.querySelector(`#${buttonId}`) as HTMLElement;
      if (element) {
        element.focus({ preventScroll: true });
      }
    },
    [],
  );

  /**
   * Focus the back button when entering certification detail view
   * @param buttonId - ID of the button to focus
   * @param shadowRoot - Optional shadow root to query within
   */
  const focusBackButton = useCallback(
    (buttonId: string, shadowRoot?: ShadowRoot | null) => {
      const root = shadowRoot || document;
      const element = root.querySelector(`#${buttonId}`) as HTMLElement;
      if (element) {
        element.focus({ preventScroll: true });
      }
    },
    [],
  );

  return {
    captureScrollPosition,
    setSheetTriggerElement,
    restoreFocusToSheetTrigger,
    focusCloseButton,
    focusBackButton,
  };
};
